import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { matricesApi } from './api';

export const DERIVE_MATRIX_ERROR_MESSAGE = 'La création depuis cette matrice a échoué. Réessayez.';

export interface UseDeriveMatrixResult {
  /**
   * Creates a draft pre-filled from `sourceMatrixId`, then opens step 1 of its
   * wizard. Settles either way and never rejects: a failure shows through
   * `errorMessage`, so a caller only awaits it to know the call is over.
   */
  deriveMatrix: (sourceMatrixId: string) => Promise<void>;
  isPending: boolean;
  /** The source being derived from while the call runs, to label the right control. */
  pendingSourceId: string | undefined;
  /** Set when the last call failed; render it inline next to the control. */
  errorMessage: string | null;
  /** Clears the error, for instance when the control that showed it goes away. */
  reset: () => void;
}

/**
 * « Nouvelle matrice depuis celle-ci » (issue #16, `docs/specs/matrices.md`
 * « Création depuis une matrice »): `POST /matrices/:id/derive`, then the
 * wizard of the new draft, with `derivedFrom` (the pre-filled banner) and
 * `pivotReset` (the pivot fell back to Levier de valeur) in its search.
 *
 * Shared by the grid's « ⋯ » menu, the matrix page's « Nouvelle matrice »
 * menu and Cadrage's link, so the three behave the same. The company's matrix
 * list is invalidated: the draft already exists when the user comes back.
 */
export function useDeriveMatrix(): UseDeriveMatrixResult {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (sourceMatrixId: string) => matricesApi.derive(sourceMatrixId),
    onSuccess: (result, sourceMatrixId) => {
      void queryClient.invalidateQueries({ queryKey: ['matrices', result.matrix.companyId] });
      void navigate({
        to: '/wizard/$matrixId',
        params: { matrixId: result.matrix.id },
        search: {
          derivedFrom: sourceMatrixId,
          pivotReset: result.pivotFellBack ? true : undefined,
        },
      });
    },
  });

  return {
    deriveMatrix: (sourceMatrixId) =>
      mutation.mutateAsync(sourceMatrixId).then(
        () => undefined,
        // Already in the mutation state, rendered through `errorMessage`.
        () => undefined,
      ),
    isPending: mutation.isPending,
    pendingSourceId: mutation.isPending ? mutation.variables : undefined,
    errorMessage: mutation.isError ? DERIVE_MATRIX_ERROR_MESSAGE : null,
    reset: mutation.reset,
  };
}
